import { ServiceType } from '@/types/bookingFlow';

/**
 * URL aliases for `/book?service=` that resolve to a bookable ServiceType.
 * Supports underscore, hyphen, and marketing-slug variants seen in GSC.
 */
export const BOOKING_SERVICE_ALIASES: Record<string, ServiceType> = {
  home_cleaning: ServiceType.HOME_CLEANING,
  'home-cleaning': ServiceType.HOME_CLEANING,
  'regular-home-cleaning': ServiceType.HOME_CLEANING,
  regular_home_cleaning: ServiceType.HOME_CLEANING,
  business_cleaning: ServiceType.BUSINESS_CLEANING,
  'business-cleaning': ServiceType.BUSINESS_CLEANING,
  office_cleaning: ServiceType.OFFICE_CLEANING,
  'office-cleaning': ServiceType.OFFICE_CLEANING,
  hoarding_cleaning: ServiceType.HOARDING_CLEANING,
  'hoarding-cleaning': ServiceType.HOARDING_CLEANING,
  move_in_out: ServiceType.MOVE_IN_OUT,
  'move-in-out': ServiceType.MOVE_IN_OUT,
  'move-in-out-cleaning': ServiceType.MOVE_IN_OUT,
  move_in_out_cleaning: ServiceType.MOVE_IN_OUT,
  post_construction: ServiceType.POST_CONSTRUCTION,
  'post-construction': ServiceType.POST_CONSTRUCTION,
  'post-construction-cleaning': ServiceType.POST_CONSTRUCTION,
  construction_cleaning: ServiceType.POST_CONSTRUCTION,
  'construction-cleaning': ServiceType.POST_CONSTRUCTION,
  window_cleaning: ServiceType.WINDOW_CLEANING,
  'window-cleaning': ServiceType.WINDOW_CLEANING,
  stairwell_cleaning: ServiceType.STAIRWELL_CLEANING,
  'stairwell-cleaning': ServiceType.STAIRWELL_CLEANING,
  furniture_transport: ServiceType.FURNITURE_TRANSPORT,
  'furniture-transport': ServiceType.FURNITURE_TRANSPORT,
  // Commercial service aliases routed through the generic business flow
  'retail-cleaning': ServiceType.BUSINESS_CLEANING,
  retail_cleaning: ServiceType.BUSINESS_CLEANING,
  'medical-practice-cleaning': ServiceType.BUSINESS_CLEANING,
  'kindergarten-cleaning': ServiceType.BUSINESS_CLEANING,
  'care-facility-cleaning': ServiceType.BUSINESS_CLEANING,
  'underground-garage-cleaning': ServiceType.BUSINESS_CLEANING,
  'trade-fair-cleaning': ServiceType.BUSINESS_CLEANING,
  'restaurant-cafe-cleaning': ServiceType.BUSINESS_CLEANING,
  'fitness-gym-cleaning': ServiceType.BUSINESS_CLEANING,
  'salon-beauty-cleaning': ServiceType.BUSINESS_CLEANING,
};

/** Business-type presets applied when a commercial alias opens the business wizard. */
export const BOOKING_BUSINESS_TYPE_ALIASES: Record<string, string> = {
  'retail-cleaning': 'retail_store',
  retail_cleaning: 'retail_store',
  'medical-practice-cleaning': 'medical_dental',
  'kindergarten-cleaning': 'school',
  'care-facility-cleaning': 'medical_dental',
  'underground-garage-cleaning': 'other',
  'trade-fair-cleaning': 'other',
  'restaurant-cafe-cleaning': 'restaurant_cafe',
  'fitness-gym-cleaning': 'fitness_gym',
  'salon-beauty-cleaning': 'salon_beauty',
};

const SERVICE_TYPE_VALUES = new Set<string>(Object.values(ServiceType));

function normalizeServiceParam(raw: string): string {
  return raw.toLowerCase().trim();
}

export function resolveBookingServiceType(raw: string | null | undefined): ServiceType | null {
  if (!raw) {
    return null;
  }

  const normalized = normalizeServiceParam(raw);
  if (SERVICE_TYPE_VALUES.has(normalized)) {
    return normalized as ServiceType;
  }

  return (
    BOOKING_SERVICE_ALIASES[normalized] ??
    BOOKING_SERVICE_ALIASES[normalized.replace(/-/g, '_')] ??
    null
  );
}

export function resolveBookingBusinessTypeAlias(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  const normalized = normalizeServiceParam(raw);
  return BOOKING_BUSINESS_TYPE_ALIASES[normalized] ?? null;
}

export function getCanonicalBookingServiceParam(serviceType: ServiceType): string {
  return serviceType;
}

export function listCanonicalBookingServiceParams(): ServiceType[] {
  return Object.values(ServiceType).sort((left, right) => left.localeCompare(right));
}
